import type { Notification } from '@/api/notifications';

/**
 * Как нарисовать уведомление: значок, заголовок, куда ведёт нажатие.
 *
 * ⚠️ ДО 10.09.2026 ВСЕ УВЕДОМЛЕНИЯ БЫЛИ НА ОДНО ЛИЦО: один значок, текст с
 * сервера, нажатие только снимало «новое». Первым своим видом стало
 * `lesson_graded` (D11): ученик должен из уведомления попасть к оценке урока.
 *
 * ⚠️ У СЛУЖБЫ УВЕДОМЛЕНИЙ НЕТ ПОЛЯ ДАННЫХ — только категория, заголовок и текст.
 * Если данные придут (`lesson_id`, `lesson_title`), нажатие раскрывает нужный
 * урок; если нет — ведёт в список уроков. Ни в одном случае не ломается.
 */

/** Чья лента: от роли зависят адреса переходов. */
export type NotificationRole = 'student' | 'curator';

export type NotificationView = {
  icon: string;
  /** Свой заголовок вместо серверного. */
  titleKey?: string;
  lessonTitle?: string;
  /** Вторая строка вместо текста с сервера — «ученик · урок». */
  detail?: string;
  /** Сдача пришла заново — из «на доработку» или «отклонено». */
  resubmitted?: boolean;
  /** Куда ведёт нажатие. */
  to?: string;
};

/** Данные уведомления: объект или строка JSON. Остальное — «данных нет». */
export function parseNotificationData(raw: unknown): Record<string, unknown> | undefined {
  let v = raw;
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v);
    } catch {
      return undefined;
    }
  }
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
}

/**
 * ⚠️ У КУРАТОРА ЛЕНТА ТА ЖЕ, А АДРЕСА ДРУГИЕ (11.09.2026). Все ученические
 * переходы ведут в `/app/...` — куратору туда нельзя. Поэтому у куратора ссылку
 * получает только своё (`homework_submitted` → «Домашка»), остальным строкам —
 * вид без перехода, нажатие лишь снимает «новое».
 */
export function describeNotification(n: Notification, role: NotificationRole = 'student'): NotificationView {
  const view = describeKind(n);
  if (role !== 'curator') return view;
  if (n.type === 'homework_submitted') {
    // Сразу в окно проверки этой сдачи (договор homework-resubmit.md §4.1,
    // решение владельца 11.09.2026). Нет id — в «Домашку» целиком.
    const sid = n.data?.submission_id;
    const to =
      typeof sid === 'string' && sid.trim()
        ? `/curator/homework?submission=${encodeURIComponent(sid)}`
        : '/curator/homework';
    return { ...view, to };
  }
  if (n.type === 'student_transferred') {
    // Куратору — в курс, где ученика забрали, к его группам (договор §9). Нет
    // курса в данных — в список марафонов: пустого перехода быть не должно.
    const marathonId = n.data?.marathon_id;
    const to =
      typeof marathonId === 'string' && marathonId.trim()
        ? `/curator/groups/${encodeURIComponent(marathonId)}`
        : '/curator/groups';
    return { ...view, to };
  }
  const rest = { ...view };
  delete rest.to;
  return rest;
}

function describeKind(n: Notification): NotificationView {
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v : undefined);

  // Сдача домашки — кураторам группы ученика (сессия progress+notification,
  // 11.09.2026): в данных ученик, урок и `resubmitted`. Адрес ставит роль выше —
  // ученику кураторская «Домашка» ни к чему.
  if (n.type === 'homework_submitted') {
    const detail = [str(n.data?.student_name), str(n.data?.lesson_title)].filter(Boolean).join(' · ');
    // Строго `true`: строка «true» или единица — не пересдача, лишняя пометка
    // заставила бы куратора искать разницу, которой нет.
    const resubmitted = n.data?.resubmitted === true;
    return {
      icon: 'assignment',
      // Заголовок как у сервера (договор §4): «Домашка пересдана» — вторая попытка.
      titleKey: resubmitted ? 'notifications.homeworkResubmitted' : 'notifications.homeworkSubmitted',
      ...(detail ? { detail } : {}),
      resubmitted,
    };
  }

  // «Ученик переведён» — прежнему куратору, когда другой куратор забрал ученика
  // в свою группу (D40, договор `curator-groups.md` §6). Ученику эта строка не
  // приходит вовсе, поэтому адрес ставится ролью выше, а не здесь.
  if (n.type === 'student_transferred') {
    const detail = [str(n.data?.student_name), str(n.data?.to_group_name)].filter(Boolean).join(' · ');
    return {
      icon: 'sync_alt',
      titleKey: 'notifications.studentTransferred',
      ...(detail ? { detail } : {}),
    };
  }

  // «Задание нужно заполнить» (договор `empty-open-answers.md`, раздел 5): сдача
  // была пустой и вернулась на доработку. Ученику нужно в сам урок, а не к оценке.
  if (n.type === 'homework_revision_required') {
    const lessonId = str(n.data?.lesson_id);
    const lessonTitle = str(n.data?.lesson_title);
    return {
      icon: 'assignment_late',
      titleKey: 'notifications.homeworkRevisionRequired',
      ...(lessonTitle ? { lessonTitle } : {}),
      to: lessonId ? `/app/lessons/${encodeURIComponent(lessonId)}` : '/app/homework',
    };
  }

  // «Куратор проверил ДЗ» (договор `push.md` §6, 01.10.2026): ученику — в сам урок, к своей работе и отзыву.
  // Заголовок и текст оставляем серверные: в них статус проверки. Нет урока в данных (раздел удалили) —
  // в «Домашку»: пустого перехода быть не должно. По этой же строке ведёт и нажатие на пуш (`pushRoute.ts`).
  if (n.type === 'homework_reviewed') {
    const lessonId = str(n.data?.lesson_id);
    return {
      icon: 'assignment',
      to: lessonId ? `/app/lessons/${encodeURIComponent(lessonId)}` : '/app/homework',
    };
  }

  if (n.type !== 'lesson_graded') return { icon: 'notifications' };
  const lessonId = str(n.data?.lesson_id);
  const marathonId = str(n.data?.marathon_id);
  const lessonTitle = str(n.data?.lesson_title);
  // ⚠️ С курсом — в страницу курса: общий список показывает только основной
  // курс, а на телефоне первые восемь уроков, и нужного урока там может не быть.
  const feedback = lessonId ? `?feedback=${encodeURIComponent(lessonId)}` : '';
  return {
    icon: 'grading',
    titleKey: 'notifications.lessonGraded',
    ...(lessonTitle ? { lessonTitle } : {}),
    to: !lessonId
      ? '/app/lessons'
      : marathonId
        ? `/app/lessons/marathon/${encodeURIComponent(marathonId)}${feedback}`
        : `/app/lessons${feedback}`,
  };
}
